import {
  Box,
  CircularProgress,
  Button as MuiButton,
  styled,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { Modal } from "components/Modal";
import { Typography } from "components/Typography";
import { useApproveTransactionMutation } from "api/transactions";
import { formatToNaira } from "utils/formatCurrency";
import * as queryKeys from "utils/queryKeys";

const Button = styled(MuiButton)(() => ({
  textTransform: "capitalize",
  marginRight: 2,
  boxShadow: "none",
  fontWeight: "400",
  marginTop: "20px",
  "&:hover": { backgroundColor: "#2D75B6", boxShadow: "none" },
}));

export const UpdateTransactionStatus = ({
  open,
  transaction,
  handleClose,
  meta,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const query = useQueryClient();

  const { mutate: approve, isPending, reset } = useApproveTransactionMutation();

  const closeModal = () => {
    handleClose();
    reset();
  };
  const handleSubmit = () => {
    approve(
      {
        data: {
          transDIds: [transaction.transDId],
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Transaction Approved!", { variant: "success" });
          query.invalidateQueries({
            queryKey: [queryKeys.transactions, meta.params],
          });
          closeModal(false);
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: "error" });
        },
      }
    );
  };

  return (
    <Modal open={open} handleClose={closeModal}>
      <Typography
        variant="emailHeading"
        align="center"
        component="h2"
        sx={{ fontSize: "20px", mb: "15px" }}
      >
        Are you sure you want to approve?
      </Typography>
      <Box my={0.8} display="flex" flexDirection="column" gap={0.8}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={0.5}
        >
          <Typography>Amount:</Typography>
          <Typography variant="loginLabel" component="h6" sx={{ fontSize: 14 }}>
            {formatToNaira(transaction?.amount)}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={0.5}
        >
          <Typography>Account Name:</Typography>
          <Typography variant="loginLabel" component="h6" sx={{ fontSize: 14 }}>
            {transaction?.accountName}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={0.5}
        >
          <Typography>Account Number:</Typography>
          <Typography variant="loginLabel" component="h6" sx={{ fontSize: 14 }}>
            {transaction?.accountNo}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={0.5}
        >
          <Typography>Bank:</Typography>
          <Typography variant="loginLabel" component="h6" sx={{ fontSize: 14 }}>
            {transaction?.bankName}
          </Typography>
        </Box>
      </Box>
      <Box my={0.8} display="flex" flexDirection="column" gap={0.8}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={0.5}
        >
          <Typography>Debit Account Number:</Typography>
          <Typography variant="loginLabel" component="h6" sx={{ fontSize: 14 }}>
            {transaction?.debitAccount || "-"}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={0.5}
        >
          <Typography>Debit Bank:</Typography>
          <Typography variant="loginLabel" component="h6" sx={{ fontSize: 14 }}>
            {transaction?.debitBankName || "-"}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" gap={3}>
        <Box flex={1}>
          <Button
            variant="outlined"
            fullWidth
            disableRipple
            onClick={closeModal}
            sx={{
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "#fff",
              },
            }}
          >
            No, cancel
          </Button>
        </Box>
        <Box flex={1}>
          <Button
            variant="contained"
            fullWidth
            disableRipple
            onClick={handleSubmit}
            sx={{ gap: 1 }}
          >
            <span>Yes, Approve</span>
            {isPending && <CircularProgress size={18} sx={{ color: "#fff" }} />}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
